import React from 'react';
import PropTypes from 'prop-types';
import _classNames from 'classnames';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { Link, useHistory } from 'react-router-dom';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { makeStyles } from '@material-ui/core/styles';
import ButtonBase from '@material-ui/core/ButtonBase';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import useSiteCopySelector from 'components/useSiteCopySelector';
import { getImageFile, fallbackImage } from 'utils/stringUtils';
import Mixpanel from 'utils/mixpanelService';
import DateAndOrgs from './DateAndOrgs';

const useStyles = makeStyles(theme => ({
  link: {
    textDecoration: 'none',
  },
  cardRoot: {
    textAlign: 'start',
    cursor: 'pointer',
    position: 'relative',
  },
  imageWrapper: {
    display: 'block',
    width: '100%',
    height: 'auto',
    position: 'relative',
    overflow: 'hidden',
    padding: '56.25% 0 0 0',
  },
  image: {
    display: 'block',
    width: '100%',
    height: '100%',
    position: 'absolute',
    objectFit: 'cover',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
  label: {
    marginTop: 18,
    ...theme.typography.pxSmall,
  },
  name: {
    display: '-webkit-box',
    margin: '0 auto',
    '-webkit-line-clamp': '3',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxHeight: 87 /* Fallback for non-webkit */,
  },
  grayscale: {
    filter: 'grayscale(1)',
  },
}));

function BigNewsCard({ data, grayscale }) {
  const classes = useStyles();
  const history = useHistory();
  const [siteCopy] = useSiteCopySelector(['resource-item-page']);
  const {
    fields: { slug, name, topics = [] },
    fields,
  } = data;
  const file = getImageFile(fields);

  const redirect = () => {
    const {
      fields: { blog },
      sys: { id },
    } = data;
    const pageName = window.location.pathname.split('/')[1];
    const finalPageName = pageName ? `${pageName}Page` : 'homePage';
    Mixpanel.track('Resource Card - Clicked', {
      name,
      type: 'article',
      subtype: 'News',
      blog,
      page: finalPageName,
      path: window.location.pathname.split('/')[1],
      slug: window.location.pathname.split('/')[2],
      entryId: id,
    });

    history.push(`/articles/${slug}`);
  };

  return (
    <ButtonBase disableRipple onClick={redirect}>
      <Grid container className={classes.cardRoot}>
        <Grid item xs={12} className={classes.imageWrapper}>
          <LazyLoadImage
            src={file}
            data-src={file}
            alt="News"
            onError={e => {
              e.target.src = fallbackImage;
            }}
            width="100%"
            height="100%"
            className={_classNames(classes.image, {
              [classes.grayscale]: grayscale,
            })}
          />
        </Grid>
        <Grid item xs={12}>
          {_isEmpty(topics) && _isEmpty(_get(topics, '0.fields')) ? (
            <Typography className={classes.label} color="textSecondary">
              {_get(siteCopy, ['pageCopy', 'resourceCardTypeMapping', 'News'])}
            </Typography>
          ) : (
            <Link
              to={`/topics/${_get(topics, '0.fields.slug')}`}
              className={classes.link}
            >
              <Typography className={classes.label} color="textSecondary">
                {_get(topics, '0.fields.title')}
              </Typography>
            </Link>
          )}
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h5" color="textPrimary" className={classes.name}>
            {name}
          </Typography>
        </Grid>
        <DateAndOrgs data={data} />
      </Grid>
    </ButtonBase>
  );
}

BigNewsCard.propTypes = {
  data: PropTypes.object,
  grayscale: PropTypes.bool,
};

export default BigNewsCard;
